import { useEffect, useState } from 'react';
import { alphaApi, type JourneyRun, type ReadinessReport, type ReleaseConfig } from '../api/alpha';

export function AlphaReadinessPage() {
  const [release, setRelease] = useState<ReleaseConfig | null>(null);
  const [readiness, setReadiness] = useState<ReadinessReport | null>(null);
  const [journeys, setJourneys] = useState<JourneyRun[]>([]);
  const [validating, setValidating] = useState(false);
  const [modules, setModules] = useState<string[]>([]);

  const load = async () => {
    try {
      const [rel, ready, runs, mods] = await Promise.all([
        alphaApi.release(),
        alphaApi.readiness(),
        alphaApi.listJourneys(),
        alphaApi.modules(),
      ]);
      setRelease(rel);
      setReadiness(ready);
      setJourneys(runs);
      setModules(mods);
    } catch {
      /* API unavailable — show offline state */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runValidation = async () => {
    setValidating(true);
    try {
      const report = await alphaApi.validateE2E();
      setReadiness(report);
      await load();
    } finally {
      setValidating(false);
    }
  };

  const statusColor = (status: string) => {
    if (status === 'pass' || status === 'completed') return 'text-green-700 bg-green-50';
    if (status === 'warn' || status === 'running') return 'text-amber-700 bg-amber-50';
    if (status === 'fail' || status === 'failed') return 'text-red-700 bg-red-50';
    return 'text-slate-700 bg-slate-50';
  };

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Alpha Readiness</h1>
        <p className="mt-1 text-sm text-slate-600">
          End-to-end integration validation for IMP-00001 through IMP-00033
        </p>
      </header>

      {release && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Release Configuration</h2>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-xs text-slate-500">Version</dt>
              <dd className="font-medium">{release.version}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Phase</dt>
              <dd className="font-medium capitalize">{release.phase}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Build</dt>
              <dd className="font-medium">{release.build_id}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Environment</dt>
              <dd className="font-medium">{release.environment}</dd>
            </div>
          </dl>
        </section>
      )}

      {readiness && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Readiness Gates</h2>
              <p className={`mt-1 inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusColor(readiness.overall)}`}>
                Overall: {readiness.overall}
              </p>
            </div>
            <button
              type="button"
              disabled={validating}
              onClick={runValidation}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {validating ? 'Validating…' : 'Run E2E Validation'}
            </button>
          </div>
          <ul className="mt-6 space-y-2">
            {readiness.gates.map((gate) => (
              <li
                key={gate.id}
                className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium">{gate.name}</p>
                  <p className="text-xs text-slate-500">{gate.module}{gate.required ? ' · required' : ''}</p>
                  {gate.message && <p className="text-xs text-amber-600">{gate.message}</p>}
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor(gate.status)}`}>
                  {gate.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold">Integrated Modules ({modules.length})</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {modules.map((m) => (
            <span key={m} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              {m}
            </span>
          ))}
        </div>
      </section>

      {journeys.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Recent Journey Runs</h2>
          <ul className="mt-4 space-y-3">
            {journeys.slice(0, 5).map((j) => (
              <li key={j.id} className="rounded-lg border border-slate-100 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{j.type.replace(/_/g, ' ')}</p>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor(j.status)}`}>
                    {j.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{j.steps.length} steps · {j.correlation_id}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
